import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore, FIRESTORE_COLLECTIONS } from '../firebase';
import { calculateAge } from './age';

export const TONYTE_PROFILE_IMAGE = require('../assets/login_logo.png');

export interface PublicUserProfile {
  id?: string;
  name?: string;
  displayName: string;
  username: string;
  profileImage: string;
  profilePhotoURL?: string;
  coverPhoto: string;
  city: string;
  location: string;
  bio: string;
  age?: number;
  dateOfBirth?: string;
  university?: string;
  lastActiveAt?: string;
  socialVibe?: string;
  interests: string[];
  eventsCount: number;
  plansCount: number;
  faceVerified?: boolean;
  isStudentVerified?: boolean;
}

const EMPTY_PROFILE: PublicUserProfile = {
  displayName: '',
  username: '',
  profileImage: '',
  coverPhoto: '',
  city: '',
  location: '',
  bio: '',
  age: 0,
  university: '',
  lastActiveAt: '',
  socialVibe: '',
  interests: [],
  eventsCount: 0,
  plansCount: 0,
  faceVerified: false,
  isStudentVerified: false,
};

const profileCache = new Map<string, PublicUserProfile>();
const profilePromiseCache = new Map<string, Promise<PublicUserProfile>>();
// Cached profiles are served immediately but re-read after this long, so a
// changed profile picture or name reaches other screens (chat avatars etc.)
// without an app restart.
const PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;
const profileFetchedAt = new Map<string, number>();

const isProfileFresh = (key: string) => (
  Date.now() - (profileFetchedAt.get(key) ?? 0) < PROFILE_CACHE_TTL_MS
);

// Drop a cached profile, e.g. right after the user edits their own profile.
export function invalidateUserProfile(userId: string | null | undefined) {
  const key = typeof userId === 'string' ? userId.trim() : '';
  if (!key) return;
  profileCache.delete(key);
  profileFetchedAt.delete(key);
}

const firstString = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length) {
      return value.trim();
    }
  }
  return '';
};

// The retired web app saved its "Tell travelers…" prompt as the bio of
// everyone who left it blank, so it sits in some users/{uid}.bio fields as if
// they had written it. Show it as no bio; real bios pass through untouched.
const LEGACY_PLACEHOLDER_BIOS = new Set(['tell travelers a little about yourself.', 'tell travelers a little about yourself']);

export function publicBio(...values: unknown[]): string {
  for (const value of values) {
    const bio = typeof value === 'string' ? value.trim() : '';
    if (bio && !LEGACY_PLACEHOLDER_BIOS.has(bio.toLowerCase())) return bio;
  }
  return '';
}

const toStringArray = (value: unknown): string[] => (
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : []
);

const normalizeUserProfile = (userId: string, data: Record<string, unknown> = {}): PublicUserProfile => ({
  id: userId,
  name: firstString(data.name, data.displayName, data.fullName),
  displayName: firstString(data.displayName, data.name, data.fullName),
  username: firstString(data.username, data.handle),
  profileImage: firstString(
    data.profilePicture,
    data.profileImage,
    data.profilePhotoURL,
    data.profilePhoto,
    data.photoURL,
    data.photo,
    data.avatar,
  ),
  profilePhotoURL: firstString(data.profilePhotoURL),
  coverPhoto: firstString(data.coverPhoto, data.coverImage, data.coverPhotoURL, data.coverImageUrl),
  city: firstString(data.locationCity, data.city, data.selectedEventCity),
  location: firstString(data.locationLabel, data.location, data.locationCity, data.city, data.country),
  bio: publicBio(data.bio, data.about),
  age: typeof data.dateOfBirth === 'string' && data.dateOfBirth
    ? calculateAge(data.dateOfBirth as string)
    : typeof data.age === 'number' ? data.age : 0,
  dateOfBirth: typeof data.dateOfBirth === 'string' ? data.dateOfBirth : undefined,
  university: firstString(data.studentUniversity, data.university),
  lastActiveAt: firstString(data.lastActiveAt),
  socialVibe: firstString(data.socialVibe),
  interests: [...toStringArray(data.interests), ...toStringArray(data.customInterests)].slice(0, 18),
  eventsCount: typeof data.eventsCount === 'number' ? data.eventsCount : 0,
  plansCount: typeof data.plansCount === 'number' ? data.plansCount : 0,
  faceVerified: data.isVibeVerified === true || data.faceVerified === true,
  isStudentVerified: data.isStudentVerified === true || data.studentVerified === true || data.studentVerificationStatus === 'verified',
});

export async function getUserProfile(userId: string | null | undefined): Promise<PublicUserProfile> {
  const key = typeof userId === 'string' ? userId.trim() : '';
  if (!key || key === 'system') return EMPTY_PROFILE;

  const cached = profileCache.get(key);
  if (cached && isProfileFresh(key)) return cached;

  const pending = profilePromiseCache.get(key);
  if (pending) return pending;

  const promise = getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, key))
    .then(snapshot => {
      const profile = snapshot.exists() ? normalizeUserProfile(key, snapshot.data()) : { ...EMPTY_PROFILE, id: key };
      profileCache.set(key, profile);
      profileFetchedAt.set(key, Date.now());
      return profile;
    })
    .catch(error => {
      console.warn('[Tonyte][RN][UserProfile] failed to load public profile', { userId: key, error });
      return profileCache.get(key) ?? EMPTY_PROFILE;
    })
    .finally(() => {
      profilePromiseCache.delete(key);
    });

  profilePromiseCache.set(key, promise);
  return promise;
}

/**
 * Existence-only check, separate from getUserProfile()'s cached/normalized
 * result — a deleted account still normalizes to a populated-looking
 * EMPTY_PROFILE shape (name "User", etc.), so callers that need to
 * distinguish "no profile to show" from "profile is just sparse" (e.g.
 * before navigating to it from a join request) need this instead.
 */
export async function userProfileExists(userId: string | null | undefined): Promise<boolean> {
  const key = typeof userId === 'string' ? userId.trim() : '';
  if (!key || key === 'system') return false;
  try {
    const snapshot = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, key));
    return snapshot.exists();
  } catch (error) {
    console.warn('[Tonyte][RN][UserProfile] failed to check profile existence', { userId: key, error });
    return false;
  }
}

export function getUserDisplayName(profile: PublicUserProfile | null | undefined): string {
  return firstString(profile?.displayName, profile?.username) || 'User';
}

export function useUserProfile(userId: string | null | undefined) {
  const stableUserId = typeof userId === 'string' && userId.trim().length ? userId.trim() : '';
  const [profile, setProfile] = useState<PublicUserProfile | null>(() => (
    stableUserId ? profileCache.get(stableUserId) ?? null : null
  ));

  useEffect(() => {
    let cancelled = false;
    if (!stableUserId) {
      setProfile(null);
      return undefined;
    }

    const cached = profileCache.get(stableUserId);
    if (cached) {
      setProfile(cached);
      if (isProfileFresh(stableUserId)) return undefined;
    }

    getUserProfile(stableUserId).then(nextProfile => {
      if (!cancelled) setProfile(nextProfile);
    });

    return () => {
      cancelled = true;
    };
  }, [stableUserId]);

  return profile;
}

export function useUserProfiles(userIds: Array<string | null | undefined>) {
  const userIdsKey = userIds
    .map(id => (typeof id === 'string' ? id.trim() : ''))
    .filter(Boolean)
    .join('|');
  const stableUserIds = useMemo(
    () => Array.from(new Set(userIdsKey.split('|').filter(Boolean))),
    [userIdsKey],
  );
  const [profiles, setProfiles] = useState<Record<string, PublicUserProfile>>({});

  useEffect(() => {
    let cancelled = false;
    if (!stableUserIds.length) {
      setProfiles({});
      return undefined;
    }

    Promise.all(stableUserIds.map(async id => [id, await getUserProfile(id)] as const)).then(entries => {
      if (cancelled) return;
      setProfiles(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
  }, [stableUserIds]);

  return profiles;
}
